import { useEffect, useState, useRef } from 'react'

import { Accordion, AccordionSummary, AccordionDetails } from './sidebar-accordion'
import { usePageEditorContext } from '../contexts/page-editor.context'
import { CategoryListEdit } from './category-list-edit'
import { reportDataSetService } from './report-data-set.service'
import { Select } from '../../common/components/select'

export const SplitChartFilterEditAccordion = function ({ block }) {
  const [defaultSelections, setDefaultSelections] = useState([])
  const currentSplitChartFilter = useRef()

  const pageEditorContext = usePageEditorContext() || {}
  const page = pageEditorContext.page

  const splitChartFilter = reportDataSetService.getSplitChartFilter(block)

  function setDefaultFilterOptions () {
    const validOptions = getValidFilterOptions()
    const defaultOption = validOptions[0]
    if (splitChartFilter && splitChartFilter.options && splitChartFilter.defaultSelections) {
      setDefaultSelections(splitChartFilter.defaultSelections.map((selectionValue) => {
        const selectedOption = splitChartFilter.options.find((option) => option.value === selectionValue)
        return selectedOption || defaultOption
      }))
    } else {
      setDefaultSelections([defaultOption, defaultOption])
    }
  }

  useEffect(() => {
    currentSplitChartFilter.current = splitChartFilter
    setDefaultFilterOptions()
  }, [])

  useEffect(() => {
    if (splitChartFilter && defaultSelections && defaultSelections.length) {
      splitChartFilter.defaultSelections = defaultSelections.map((selection) => selection && selection.value)
    }
  }, [defaultSelections])

  useEffect(() => {
    if (splitChartFilter !== currentSplitChartFilter.current) {
      // Reset default selections since the split chart filter changed
      setDefaultFilterOptions()
      currentSplitChartFilter.current = splitChartFilter
    }

    if (splitChartFilter && splitChartFilter.options && splitChartFilter.defaultSelections) {
      const validOptions = getValidFilterOptions()
      splitChartFilter.defaultSelections.forEach((selection, i) => {
        if (!validOptions.find((option) => option.value === selection)) {
          const newSelection = validOptions.find((option) => option.value === null) || validOptions[0]
          updateDefaultSplitChartSelection(newSelection, i)
        }
      })
    }
  }, [splitChartFilter])

  function getValidFilterOptions () {
    return splitChartFilter?.options.filter((option) => !option.hide) || []
  }

  function updateDefaultSplitChartSelection (value, i) {
    const newSelections = [...defaultSelections]
    newSelections[i] = value
    setDefaultSelections(newSelections)
    const updatedSubgroups = [...reportDataSetService.getFilterOptionLabels(block)]
    const splitChartSubgroup = updatedSubgroups.find((subgroup) => subgroup.key === splitChartFilter.key && subgroup.isSplitChartFilter)
    if (splitChartSubgroup) {
      splitChartSubgroup.defaultSelections = newSelections.map((selection) => selection.value)
      block = reportDataSetService.updateFilterOptionLabels(block, updatedSubgroups)
      pageEditorContext.registerUnsavedChanges()
      pageEditorContext.updateCurrentEdit({ block: { ...block } })
    }
  }

  if (!splitChartFilter) {
    return null
  }

  return (
    <Accordion square data-qa="split-chart-settings-edit-accordion">
      <AccordionSummary data-qa="split-chart-settings-accordion-trigger">
        Split Chart Settings
      </AccordionSummary>
      <AccordionDetails>
        <div className="field-grid">
          <div className="full-width-field">
            <CategoryListEdit
              size="medium"
              hideCategoryDropdown
              listLabel={page && page.isInteractive ? 'Filter Option Labels' : 'Filter Value'}
              isSingleSelection={page && !page.isInteractive}
              categories={reportDataSetService.getFilterOptionLabels(block).filter((field) => field.isSplitChartFilter)}
              onChange={(updatedSubgroups) => {
                // Merge split chart filter back in with the rest of filters before running update.
                // Otherwise, the other filters get deleted from the category list
                updatedSubgroups = [
                  ...updatedSubgroups,
                  ...reportDataSetService.getFilterOptionLabels(block).filter((field) => !field.isSplitChartFilter)
                ]
                block = reportDataSetService.updateFilterOptionLabels(block, updatedSubgroups)
                pageEditorContext.registerUnsavedChanges()
                pageEditorContext.updateCurrentEdit({ block: { ...block } })
              }}
            >
              {({ classes }) => (
                <>
                  <div data-qa="split-chart-settings-left-value-select">
                    <Select
                      className={classes.select}
                      label="Default Left Value"
                      options={splitChartFilter.options.filter((option) => !option.hide)}
                      size="medium"
                      value={defaultSelections[0]}
                      onChange={(value) => {
                        updateDefaultSplitChartSelection(value, 0)
                      }}
                    />
                  </div>
                  <div data-qa="split-chart-settings-right-value-select">
                    <Select
                      className={classes.select}
                      label="Default Right Value"
                      options={splitChartFilter.options.filter((option) => !option.hide)}
                      size="medium"
                      value={defaultSelections[1]}
                      onChange={(value) => {
                        updateDefaultSplitChartSelection(value, 1)
                      }}
                    />
                  </div>
                </>
              )}
            </CategoryListEdit>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  )
}
